import type { ReactNode } from "react";
import { fontAttrs } from "@/components/tools/printables/page-kit";
import {
  type HeaderField,
  MUTED,
  SheetHeader,
} from "@/components/tools/printables/sheet-header";
import {
  KeySheet,
  PracticeSheet,
  TestSheet,
} from "@/components/tools/printables/spelling-sheets";
import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import {
  layoutKey,
  layoutPractice,
  layoutTest,
  practiceColumns,
  type SpellingEntry,
  spellingHeaderHeight,
} from "@/lib/tool-kit/printables/spelling";
import type { TextMeasure } from "@/lib/tool-kit/printables/text";

/** The strings the sheets print. */
export interface SpellingSheetStrings {
  defaultTitle: string;
  practiceSuffix: string;
  keySuffix: string;
  nameLabel: string;
  dateLabel: string;
  scoreLabel: string;
  stepLook: string;
  stepWrite: string;
  stepCheck: string;
  sentenceLabel: string;
  keyInstruction: string;
}

export interface SpellingBuild {
  geometry: PageGeometry;
  measure: TextMeasure;
  strings: SpellingSheetStrings;
  title: string;
  nameLines: boolean;
  /** Entries as typed, and in the order the test uses. */
  words: readonly SpellingEntry[];
  ordered: readonly SpellingEntry[];
  empty: boolean;
  spacing: { test: number; practice: number };
  columns: 1 | 2;
  sentenceLines: number;
  writeBoxes: number;
  trace: boolean;
  sheets: { test: boolean; practice: boolean; key: boolean };
}

export const BLANK_TEST_WORDS = 20;
export const BLANK_PRACTICE_ROWS = 10;

function blankEntries(count: number): SpellingEntry[] {
  return Array.from({ length: count }, () => ({ word: "", sentence: "" }));
}

function testNodes(b: SpellingBuild): ReactNode[] {
  const { strings } = b;
  const count = b.empty ? BLANK_TEST_WORDS : b.ordered.length;
  const fields: HeaderField[] = b.nameLines
    ? [
        { label: strings.nameLabel, share: 5 },
        { label: strings.dateLabel, share: 3 },
        { label: strings.scoreLabel, share: 2 },
      ]
    : [];
  const pages = layoutTest(
    count,
    {
      columns: b.columns,
      rowHeight: b.spacing.test,
      sentenceLines: b.sentenceLines,
      headerHeight: spellingHeaderHeight(b.title.length > 0, b.nameLines),
    },
    b.geometry
  );
  return pages.map((page, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: sheets are positional
    <g key={`test-${index}`}>
      {page.first ? (
        <SheetHeader
          fields={fields}
          geometry={b.geometry}
          measure={b.measure}
          title={b.title}
        />
      ) : null}
      <TestSheet
        geometry={b.geometry}
        page={page}
        rowHeight={b.spacing.test}
        sentenceLabel={strings.sentenceLabel}
      />
    </g>
  ));
}

function practiceNodes(b: SpellingBuild): ReactNode[] {
  const { strings } = b;
  const entries = b.empty ? blankEntries(BLANK_PRACTICE_ROWS) : b.words;
  const fields: HeaderField[] = b.nameLines
    ? [
        { label: strings.nameLabel, share: 6 },
        { label: strings.dateLabel, share: 3 },
      ]
    : [];
  const pages = layoutPractice(
    entries.length,
    {
      writeColumns: b.writeBoxes,
      rowHeight: b.spacing.practice,
      headerHeight: spellingHeaderHeight(true, b.nameLines),
    },
    b.geometry
  );
  const columns = practiceColumns(b.geometry, b.writeBoxes);
  const title = `${b.title || strings.defaultTitle} — ${strings.practiceSuffix}`;
  return pages.map((page, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: sheets are positional
    <g key={`practice-${index}`}>
      {page.first ? (
        <SheetHeader
          fields={fields}
          geometry={b.geometry}
          measure={b.measure}
          title={title}
        />
      ) : null}
      <PracticeSheet
        columns={columns}
        entries={entries}
        geometry={b.geometry}
        labels={{
          look: strings.stepLook,
          write: strings.stepWrite,
          check: strings.stepCheck,
        }}
        measure={b.measure}
        page={page}
        rowHeight={b.spacing.practice}
        trace={b.trace}
      />
    </g>
  ));
}

function keyNodes(b: SpellingBuild): ReactNode[] {
  const { strings } = b;
  const hasSentences = b.ordered.some((entry) => entry.sentence);
  const rowHeight = hasSentences ? 17 : 9;
  const title = `${b.title || strings.defaultTitle} — ${strings.keySuffix}`;
  const pages = layoutKey(
    b.ordered,
    rowHeight,
    spellingHeaderHeight(true, false),
    b.geometry
  );
  return pages.map((page, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: sheets are positional
    <g key={`key-${index}`}>
      {page.first ? (
        <>
          <SheetHeader
            fields={[]}
            geometry={b.geometry}
            measure={b.measure}
            title={title}
          />
          <text
            fill={MUTED}
            fontSize={3.6}
            x={b.geometry.left}
            y={b.geometry.top + 11.5}
            {...fontAttrs({ kind: "sans" })}
          >
            {strings.keyInstruction}
          </text>
        </>
      ) : null}
      <KeySheet entries={b.ordered} measure={b.measure} page={page} />
    </g>
  ));
}

/** Every sheet's SVG contents: the test, the practice grid, then the teacher's copy. */
export function buildSpellingPages(b: SpellingBuild): ReactNode[] {
  return [
    ...(b.sheets.test ? testNodes(b) : []),
    ...(b.sheets.practice ? practiceNodes(b) : []),
    ...(b.sheets.key && !b.empty ? keyNodes(b) : []),
  ];
}
